import React, { useState, useEffect } from 'react';
import { 
  TextField, 
  Button, 
  Box, 
  Typography, 
  Select, 
  MenuItem, 
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parse, isValid } from 'date-fns';
import heLocale from 'date-fns/locale/he';
import { CameraAlt, Upload, Search } from '@mui/icons-material';
import PassportScanner from './PassportScanner';
import { initiatePayment, searchTourists } from '../api';
import { useAuth } from '../auth/useAuth';

function UnifiedManualInput({ context, busId, busDetails, onSubmit, systemSettings }) {
  const { api } = useAuth();
  
  // מצב כללי
  const [mode, setMode] = useState(
    context === 'bus-validation' ? 'new' : 'existing'
  ); // במצב תיקוף נוסעים מתחיל במצב חדש, אחרת במצב חיפוש
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  
  // נתוני הטופס
  const [formData, setFormData] = useState({
    idNumber: '',
    lastNameEn: '',
    firstNameEn: '',
    birthDate: null,
    passportNumber: '',
    citizenship: 'ישראלי',
    paymentMethod: 'מזומן',
    originOrDestination: ''
  });
  
  // מצבי סריקה ותשלום
  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const [isCameraScannerOpen, setIsCameraScannerOpen] = useState(false);
  const [isFileScannerOpen, setIsFileScannerOpen] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // חישוב מחיר אוטומטי בתיקוף נוסעים כשנתוני האוטובוס נטענים
  useEffect(() => {
    if (context === 'bus-validation' && busDetails && systemSettings) {
      console.log('🚌 Bus details:', busDetails);
      console.log('⚙️ System settings:', systemSettings);
      
      const isOutbound = busDetails.direction === 'H' || busDetails.direction === 'הלוך';
      // בהלוך - מחפש מחיר לפי מוצא, בחזור - מחפש מחיר לפי יעד
      const priceKey = isOutbound ? busDetails.origin : busDetails.destination;
      
      console.log(`📍 Direction: ${busDetails.direction}, IsOutbound: ${isOutbound}`);
      console.log(`🗺️ Origin: ${busDetails.origin}, Destination: ${busDetails.destination}`);
      console.log(`🔑 Price key: ${priceKey}`);
      
      if (priceKey) {
        // חישוב מחיר ישירות כאן כדי לא ליצור תלות מעגלית
        const priceSettings = isOutbound 
          ? systemSettings?.find(s => s.key === 'departure_prices')?.value || {}
          : systemSettings?.find(s => s.key === 'return_prices')?.value || {};
        
        console.log('💰 Price settings:', priceSettings);
        console.log(`🔍 Looking for price for key: "${priceKey}"`);
        
        const price = priceSettings[priceKey] || 0;
        console.log(`💵 Calculated price: ${price}`);
        
        setCalculatedPrice(price);
      }
    }
  }, [busDetails, systemSettings, context]);

  // טיפול בשינויים בטופס
  // פונקציה לניקוי הטופס
  const clearForm = () => {
    setFormData({
      idNumber: '',
      lastNameEn: '',
      firstNameEn: '',
      birthDate: null,
      passportNumber: '',
      citizenship: 'ישראלי',
      paymentMethod: 'מזומן',
      originOrDestination: ''
    });
    setCalculatedPrice(0);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // חישוב מחיר ברשימת תיירים או בתיקוף נוסעים
    if (name === 'originOrDestination' && (context === 'tourist-list' || context === 'bus-validation')) {
      calculatePrice(value);
    }
  };

  // קבלת אפשרויות יעד/מוצא בהתאם למצב הנסיעה
  const getDestinationOptions = () => {
    if (context === 'bus-validation' && busDetails) {
      // בתיקוף נוסעים, החזר את המקום הרלוונטי למחיר
      const isOutbound = busDetails.direction === 'H' || busDetails.direction === 'הלוך';
      const priceKey = isOutbound ? busDetails.origin : busDetails.destination;
      return priceKey ? [priceKey] : [];
    }
    
    if (!systemSettings || context !== 'tourist-list') {
      return [];
    }

    const travelMode = systemSettings?.find(s => s.key === 'travel_mode')?.value;
    const isOutbound = travelMode === 'הלוך';
    
    const priceSettings = isOutbound 
      ? systemSettings?.find(s => s.key === 'departure_prices')?.value || {}
      : systemSettings?.find(s => s.key === 'return_prices')?.value || {};
    
    return Object.keys(priceSettings);
  };

  // חישוב מחיר
  const calculatePrice = (destination) => {
    if (!destination) {
      setCalculatedPrice(0);
      return;
    }

    if (context === 'bus-validation' && busDetails && systemSettings) {
      // בתיקוף נוסעים, חשב מחיר לפי כיוון האוטובוס
      const isOutbound = busDetails.direction === 'H' || busDetails.direction === 'הלוך';
      const priceKey = isOutbound ? busDetails.origin : busDetails.destination;
      
      const priceSettings = isOutbound 
        ? systemSettings?.find(s => s.key === 'departure_prices')?.value || {}
        : systemSettings?.find(s => s.key === 'return_prices')?.value || {};
      
      const price = priceSettings[priceKey] || 0;
      setCalculatedPrice(price);
      return;
    }

    if (!systemSettings || context !== 'tourist-list') {
      setCalculatedPrice(0);
      return;
    }

    const travelMode = systemSettings?.find(s => s.key === 'travel_mode')?.value;
    const isOutbound = travelMode === 'הלוך';
    
    const priceSettings = isOutbound 
      ? systemSettings?.find(s => s.key === 'departure_prices')?.value || {}
      : systemSettings?.find(s => s.key === 'return_prices')?.value || {};
    
    const price = priceSettings[destination] || 0;
    setCalculatedPrice(price);
  };

  // חיפוש תיירים קיימים
  const handleSearch = async (searchField) => {
    if (formData[searchField].length >= 3) {
      setIsSearching(true);
      try {
        const results = await searchTourists(formData[searchField]);
        if (results && results.tourists && results.tourists.length > 0) {
          const tourist = results.tourists[0];
          // נקה את כל הטופס ומלא נתונים חדשים
          clearForm();
          setFormData({
            idNumber: tourist.TZ || '',
            lastNameEn: tourist.LAST_NAME || tourist.MISHPACHA || '',
            firstNameEn: tourist.FIRST_NAME || tourist.PRATI || '',
            birthDate: tourist.BIRTH_DATE ? parse(tourist.BIRTH_DATE, 'yyyy-MM-dd', new Date()) : null,
            passportNumber: tourist.PASSPORT_NUMBER || '',
            citizenship: tourist.TZ ? 'ישראלי' : 'אחר', // אם יש ת.ז = ישראלי
            paymentMethod: 'מזומן',
            originOrDestination: ''
          });
        } else {
          alert('לא נמצאו תוצאות');
        }
      } catch (error) {
        console.error('Error searching for tourists:', error);
        alert('אירעה שגיאה בחיפוש');
      } finally {
        setIsSearching(false);
      }
    } else {
      alert('נא להזין לפחות 3 תווים');
    }
  };

  // טיפול בסריקת דרכון
  const handleScanComplete = (scannedData) => {
    console.log('🔄 handleScanComplete called with data:', scannedData);
    // נקה את כל הטופס ומלא נתונים חדשים מהסריקה
    console.log('🧹 Clearing form before filling new data...');
    clearForm();
    console.log('📝 Setting new form data:', scannedData);
    setFormData(prev => ({
      ...prev,
      ...scannedData // מעכתב עם הנתונים הסרוקים
    }));
    
    // בתיקוף נוסעים - חשב מחדש את המחיר לאחר סריקת דרכון
    if (context === 'bus-validation' && busDetails && systemSettings) {
      console.log('💰 Recalculating price after passport scan...');
      const isOutbound = busDetails.direction === 'H' || busDetails.direction === 'הלוך';
      const priceKey = isOutbound ? busDetails.origin : busDetails.destination;
      
      if (priceKey) {
        const priceSettings = isOutbound 
          ? systemSettings?.find(s => s.key === 'departure_prices')?.value || {}
          : systemSettings?.find(s => s.key === 'return_prices')?.value || {};
        
        const price = priceSettings[priceKey] || 0;
        console.log(`💵 Recalculated price after scan: ${price}`);
        setCalculatedPrice(price);
      }
    }
    
    setIsCameraScannerOpen(false);
    setIsFileScannerOpen(false);
  };

  // טיפול בתאריך
  const handleDateChange = (date) => {
    setFormData(prev => ({ ...prev, birthDate: date }));
  };

  // טיפול בתשלום באשראי
  const handleCreditPayment = async (orderData) => {
    setIsProcessingPayment(true);
    try {
      const paymentData = {
        amount: calculatedPrice,
        currency: "2",
        description: `תשלום עבור נסיעה ל${orderData.originOrDestination}`,
        paramX: `manual_order_${Date.now()}`,
      };

      const paymentResponse = await initiatePayment(paymentData);
      
      if (paymentResponse.success) {
        window.open(paymentResponse.paymentUrl, '_blank');
        setIsLoading(true);
        await onSubmit(orderData);
      } else {
        throw new Error('Payment initiation failed');
      }
    } catch (error) {
      console.error('שגיאה בתשלום באשראי:', error);
      alert('שגיאה בעיבוד התשלום. אנא נסה שוב.');
    } finally {
      setIsProcessingPayment(false);
      setIsLoading(false);
    }
  };

  // שליחת הטופס
  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      
      const submissionData = {
        ...formData,
        birthDate: formData.birthDate ? format(formData.birthDate, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx") : null,
      };

      // הוסף נתונים ספציפיים להקשר
      if (context === 'tourist-list') {
        submissionData.calculatedPrice = calculatedPrice;
        submissionData.paymentMethod = formData.paymentMethod;
        
        // טיפול בתשלום באשראי
        if (formData.paymentMethod === 'אשראי' && calculatedPrice > 0) {
          await handleCreditPayment(submissionData);
          return;
        }
      }

      if (context === 'bus-validation') {
        submissionData.busId = busId;
        submissionData.calculatedPrice = calculatedPrice;
        submissionData.paymentMethod = formData.paymentMethod;
        // וודא שהמקום הרלוונטי למחיר מועבר נכון מנתוני האוטובוס
        const isOutbound = busDetails?.direction === 'H' || busDetails?.direction === 'הלוך';
        submissionData.originOrDestination = isOutbound 
          ? busDetails.origin 
          : busDetails.destination;
      }

      await onSubmit(submissionData);
    } catch (error) {
      console.error('שגיאה בשליחת הטופס:', error);
      alert('שגיאה בעיבוד הבקשה. אנא נסה שוב.');
    } finally {
      setIsLoading(false);
    }
  };

  // קבלת טקסט הכפתור
  const getSubmitButtonText = () => {
    if (isProcessingPayment) return '⏳ מעבד תשלום...';
    if (isLoading) return '📝 מעבד...';
    
    if (context === 'bus-validation') {
      return 'יצור הזמנה ותקף נוסע';
    } else {
      return 'צור הזמנה';
    }
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h6" gutterBottom>
        {context === 'bus-validation' ? 'הזנת פרטי נוסע ידנית' : 'הוספת הזמנה ידנית'}
      </Typography>

      {/* בחירת מצב - זמין בכל ההקשרים */}
      <>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(event, newMode) => {
              if (newMode) {
                clearForm(); // נקה את הטופס כשעוברים בין מצבים
                setMode(newMode);
              }
            }}
            sx={{ mb: 2, width: '100%' }}
          >
            <ToggleButton value="new" sx={{ flex: 1 }}>
              לקוח חדש
            </ToggleButton>
            <ToggleButton value="existing" sx={{ flex: 1 }}>
              לקוח קיים
            </ToggleButton>
          </ToggleButtonGroup>
          <Divider sx={{ mb: 2 }} />
        </>

      {/* חיפוש תיירים קיימים */}
      {mode === 'existing' && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            חיפוש תייר קיים
          </Typography>
          
          <Select
            fullWidth
            name="citizenship"
            value={formData.citizenship}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
          >
            <MenuItem value="ישראלי">ישראלי</MenuItem>
            <MenuItem value="אחר">אחר</MenuItem>
          </Select>

          {formData.citizenship === 'ישראלי' && (
            <>
              <TextField
                margin="normal"
                fullWidth
                name="idNumber"
                label="תעודת זהות"
                value={formData.idNumber}
                onChange={handleInputChange}
                sx={{ mb: 1 }}
              />
              <Button
                fullWidth
                variant="outlined"
                startIcon={isSearching ? <CircularProgress size={16} /> : <Search />}
                onClick={() => handleSearch('idNumber')}
                disabled={isSearching || isLoading}
                sx={{ mb: 2 }}
              >
                {isSearching ? 'מחפש...' : 'חפש לפי תעודת זהות'}
              </Button>
            </>
          )}

          <TextField
            margin="normal"
            fullWidth
            name="passportNumber"
            label="מספר דרכון"
            value={formData.passportNumber}
            onChange={handleInputChange}
            sx={{ mb: 1 }}
          />
          <Button
            fullWidth
            variant="outlined"
            startIcon={isSearching ? <CircularProgress size={16} /> : <Search />}
            onClick={() => handleSearch('passportNumber')}
            disabled={isSearching || isLoading}
            sx={{ mb: 2 }}
          >
            {isSearching ? 'מחפש...' : 'חפש לפי מספר דרכון'}
          </Button>

          <Divider sx={{ my: 2 }} />
        </Box>
      )}

      {/* סריקת דרכון - זמין תמיד בtourist-list, במצב לקוח חדש, או בתיקוף נוסעים */}
      {(context === 'tourist-list' || mode === 'new' || context === 'bus-validation') && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            סריקת דרכון
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<CameraAlt />}
              onClick={() => setIsCameraScannerOpen(true)}
              disabled={isProcessingPayment || isLoading}
              sx={{ flex: 1 }}
            >
              📷 צלם דרכון
            </Button>
            <Button
              variant="outlined"
              startIcon={<Upload />}
              onClick={() => setIsFileScannerOpen(true)}
              disabled={isProcessingPayment || isLoading}
              sx={{ flex: 1 }}
            >
              📁 העלה תמונה
            </Button>
          </Box>
          <Divider sx={{ my: 2 }} />
        </Box>
      )}

      {/* פרטי התייר */}
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        פרטי התייר
      </Typography>

      {formData.citizenship !== 'ישראלי' && (
        <Select
          fullWidth
          name="citizenship"
          value={formData.citizenship}
          onChange={handleInputChange}
          sx={{ mb: 2 }}
        >
          <MenuItem value="ישראלי">ישראלי</MenuItem>
          <MenuItem value="אחר">אחר</MenuItem>
        </Select>
      )}

      {formData.citizenship === 'ישראלי' && !formData.idNumber && (
        <TextField
          margin="normal"
          fullWidth
          name="idNumber"
          label="תעודת זהות"
          value={formData.idNumber}
          onChange={handleInputChange}
        />
      )}

      <TextField
        margin="normal"
        required
        fullWidth
        name="lastNameEn"
        label="שם משפחה באנגלית"
        value={formData.lastNameEn}
        onChange={handleInputChange}
      />

      <TextField
        margin="normal"
        required
        fullWidth
        name="firstNameEn"
        label="שם פרטי באנגלית"
        value={formData.firstNameEn}
        onChange={handleInputChange}
      />

      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
        <DatePicker
          label="תאריך לידה"
          value={formData.birthDate}
          onChange={handleDateChange}
          renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
          format="dd/MM/yyyy"
        />
      </LocalizationProvider>

      <TextField
        margin="normal"
        fullWidth
        name="passportNumber"
        label="מספר דרכון"
        value={formData.passportNumber}
        onChange={handleInputChange}
      />

      {/* יעד ומחיר - ברשימת תיירים או בתיקוף נוסעים */}
      {(context === 'tourist-list' || context === 'bus-validation') && (
        <>
          {context === 'bus-validation' && busDetails ? (
            // בתיקוף נוסעים - שדה לא ניתן לעריכה עם היעד של האוטובוס
            <TextField
              margin="normal"
              fullWidth
              name="originOrDestination"
              label={(busDetails.direction === 'H' || busDetails.direction === 'הלוך') ? 'מוצא (למחיר)' : 'יעד (למחיר)'}
              value={(busDetails.direction === 'H' || busDetails.direction === 'הלוך') ? busDetails.origin : busDetails.destination}
              disabled
              sx={{ mt: 2, mb: 1 }}
              helperText="מקום קבוע לפי נתוני האוטובוס - לחישוב מחיר"
            />
          ) : (
            // ברשימת תיירים - dropdown רגיל
            <Select
              fullWidth
              name="originOrDestination"
              value={formData.originOrDestination}
              onChange={handleInputChange}
              displayEmpty
              sx={{ mt: 2, mb: 1 }}
            >
              <MenuItem value="" disabled>
                {(() => {
                  const travelMode = systemSettings?.find(s => s.key === 'travel_mode')?.value;
                  return travelMode === 'הלוך' ? 'בחר יעד' : 'בחר מוצא';
                })()}
              </MenuItem>
              {getDestinationOptions().map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          )}

          {calculatedPrice > 0 && (
            <Box 
              sx={{ 
                mt: 2, 
                p: 2, 
                backgroundColor: 'primary.light', 
                borderRadius: 1,
                textAlign: 'center'
              }}
            >
              <Typography variant="h6" color="primary.contrastText">
                מחיר הנסיעה: ${calculatedPrice}
              </Typography>
            </Box>
          )}

          {/* שיטת תשלום רק ברשימת תיירים */}
          {context === 'tourist-list' && (
            <>
              <Select
                fullWidth
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleInputChange}
                sx={{ mt: 2, mb: 1 }}
              >
                <MenuItem value="מזומן">מזומן</MenuItem>
                <MenuItem value="פאייכלה">פאייכלה</MenuItem>
                <MenuItem value="אשראי">אשראי</MenuItem>
              </Select>

              {formData.paymentMethod === 'אשראי' && calculatedPrice > 0 && !isProcessingPayment && (
                <Box 
                  sx={{ 
                    mt: 1, 
                    p: 1, 
                    backgroundColor: 'success.light', 
                    borderRadius: 1,
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="body2" color="success.contrastText">
                    תשלום באשראי - לחץ על הכפתור להמשך לתשלום
                  </Typography>
                </Box>
              )}
            </>
          )}
        </>
      )}

      {/* שיטת תשלום בהקשר של אוטובוס */}
      {context === 'bus-validation' && (
        <Select
          fullWidth
          name="paymentMethod"
          value={formData.paymentMethod}
          onChange={handleInputChange}
          sx={{ mt: 2, mb: 1 }}
        >
          <MenuItem value="מזומן">מזומן</MenuItem>
          <MenuItem value="פאייכלה">פאייכלה</MenuItem>
          <MenuItem value="אשראי">אשראי</MenuItem>
        </Select>
      )}

      {/* כפתור שליחה */}
      <Button
        fullWidth
        variant="contained"
        onClick={handleSubmit}
        disabled={isProcessingPayment || isLoading || isSearching}
        sx={{ mt: 3 }}
      >
        {getSubmitButtonText()}
      </Button>

      {/* סורקי דרכון */}
      <PassportScanner
        open={isCameraScannerOpen}
        onClose={() => setIsCameraScannerOpen(false)}
        onScanComplete={handleScanComplete}
        mode="camera"
      />
      
      <PassportScanner
        open={isFileScannerOpen}
        onClose={() => setIsFileScannerOpen(false)}
        onScanComplete={handleScanComplete}
        mode="file"
      />
    </Box>
  );
}

export default UnifiedManualInput;
